import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createClient } from '@supabase/supabase-js';
import { IconPlus, IconSearch, IconUpload, IconMic, IconClose, IconCheck, IconTrash, IconEdit } from './Icons';
import { useAuth } from '../contexts/AuthContext';
import './Notes.css';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
const noOpLock = async (_name, _acquireTimeout, fn) => fn();
const notesSupabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true, lock: noOpLock },
});

const FETCH_TIMEOUT_MS = 12000;
const NOTE_ATTACHMENTS_BUCKET = 'note-attachments';
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, { dateStyle: 'short' });
  } catch {
    return iso;
  }
}

export default function Notes() {
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const editFileInputRef = useRef(null);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [showLabelsModal, setShowLabelsModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', labels: '' });
  const [selectedFile, setSelectedFile] = useState(null);
  const [attachmentRemoved, setAttachmentRemoved] = useState(false);

  const labelPalette = [
    '#22c55e',
    '#10b981',
    '#0ea5e9',
    '#38bdf8',
    '#6b7280',
    '#e5e7eb',
    '#facc15',
    '#fb923c',
    '#f97316',
    '#ef4444',
    '#ec4899',
    '#6366f1',
    '#111827',
    '#a855f7',
  ];

  const [selectedColor, setSelectedColor] = useState(labelPalette[0]);
  const [labelName, setLabelName] = useState('');
  const [labels, setLabels] = useState([]);
  const mountedRef = useRef(true);

  const runWakeUp = useCallback(() => {
    return Promise.race([
      notesSupabase.from('profiles').select('id').limit(1),
      new Promise((_, reject) => setTimeout(() => reject(new Error('wakeup')), 5000)),
    ]).catch(() => {});
  }, []);

  const fetchNotes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const fetchPromise = notesSupabase
        .from('notes')
        .select('*')
        .order('created_at', { ascending: false });
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timed out. Try refreshing.')), FETCH_TIMEOUT_MS)
      );
      const result = await Promise.race([fetchPromise, timeoutPromise]);
      const { data, err } = result;
      if (err) {
        setError(err.message);
        setNotes([]);
      } else {
        setNotes(data ?? []);
      }
    } catch (e) {
      setError(e?.message || 'Failed to load notes.');
      setNotes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshSessionAndFetch = useCallback(async () => {
    mountedRef.current = true;
    setError(null);
    try {
      await notesSupabase.auth.getSession();
      await runWakeUp();
      await fetchNotes();
    } catch (e) {
      setError(e?.message || 'Failed to load');
    }
  }, [runWakeUp, fetchNotes]);

  useEffect(() => {
    let cancelled = false;
    mountedRef.current = true;
    (async () => {
      const { error: sessionErr } = await notesSupabase.auth.getSession();
      if (cancelled) return;
      if (sessionErr) {
        setError(sessionErr.message);
        setLoading(false);
        return;
      }
      await runWakeUp();
      if (cancelled) return;
      await fetchNotes();
    })();
    return () => { mountedRef.current = false; };
  }, [runWakeUp, fetchNotes]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      refreshSessionAndFetch();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refreshSessionAndFetch]);

  const filteredNotes = notes.filter((n) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    const title = (n.title ?? '').toLowerCase();
    const description = (n.description ?? '').toLowerCase();
    const labelsStr = (n.labels ?? '').toLowerCase();
    return title.includes(q) || description.includes(q) || labelsStr.includes(q);
  });

  const openAddModal = () => {
    setForm({ title: '', description: '', labels: '' });
    setSelectedFile(null);
    setShowAddModal(true);
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setForm({ title: '', description: '', labels: '' });
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const openEditModal = (note) => {
    setEditingNote(note);
    setForm({
      title: note.title ?? '',
      description: note.description ?? '',
      labels: note.labels ?? '',
    });
    setSelectedFile(null);
    setAttachmentRemoved(false);
    if (editFileInputRef.current) editFileInputRef.current.value = '';
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingNote(null);
    setForm({ title: '', description: '', labels: '' });
    setSelectedFile(null);
    setAttachmentRemoved(false);
    if (editFileInputRef.current) editFileInputRef.current.value = '';
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setError(`File must be 5 MB or smaller (${(file.size / 1024 / 1024).toFixed(1)} MB selected).`);
      setSelectedFile(null);
      e.target.value = '';
      return;
    }
    setSelectedFile(file);
  };

  const handleSaveNote = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await runWakeUp();
      let attachmentUrl = null;
      if (selectedFile && user?.id) {
        const safeName = selectedFile.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const path = `${user.id}/${Date.now()}_${safeName}`;
        const { error: uploadErr } = await notesSupabase.storage
          .from(NOTE_ATTACHMENTS_BUCKET)
          .upload(path, selectedFile, { upsert: false });
        if (uploadErr) {
          setError(uploadErr.message || 'Failed to upload file.');
          setSaving(false);
          return;
        }
        const { data: { publicUrl } } = notesSupabase.storage
          .from(NOTE_ATTACHMENTS_BUCKET)
          .getPublicUrl(path);
        attachmentUrl = publicUrl;
      }
      const { error: err } = await notesSupabase.from('notes').insert({
        title: form.title.trim() || null,
        description: form.description.trim() || null,
        labels: form.labels.trim() || null,
        attachment_url: attachmentUrl,
      });
      if (err) {
        setError(err.message);
        return;
      }
      closeAddModal();
      fetchNotes();
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateNote = async (e) => {
    e.preventDefault();
    if (!editingNote) return;
    setSaving(true);
    setError(null);
    try {
      await runWakeUp();
      let attachmentUrl = attachmentRemoved ? null : (editingNote.attachment_url ?? null);
      if (selectedFile && user?.id) {
        const safeName = selectedFile.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const path = `${user.id}/${Date.now()}_${safeName}`;
        const { error: uploadErr } = await notesSupabase.storage
          .from(NOTE_ATTACHMENTS_BUCKET)
          .upload(path, selectedFile, { upsert: false });
        if (uploadErr) {
          setError(uploadErr.message || 'Failed to upload file.');
          setSaving(false);
          return;
        }
        const { data: { publicUrl } } = notesSupabase.storage
          .from(NOTE_ATTACHMENTS_BUCKET)
          .getPublicUrl(path);
        attachmentUrl = publicUrl;
      }
      const { error: err } = await notesSupabase
        .from('notes')
        .update({
          title: form.title.trim() || null,
          description: form.description.trim() || null,
          labels: form.labels.trim() || null,
          attachment_url: attachmentUrl,
          updated_at: new Date().toISOString(),
        })
        .eq('id', editingNote.id);
      if (err) {
        setError(err.message);
        return;
      }
      closeEditModal();
      fetchNotes();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (note) => setDeleteConfirm(note);

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    setSaving(true);
    try {
      await runWakeUp();
      const { error: err } = await notesSupabase.from('notes').delete().eq('id', deleteConfirm.id);
      if (err) setError(err.message);
      else fetchNotes();
    } finally {
      setSaving(false);
      setDeleteConfirm(null);
    }
  };

  const COLUMNS = [
    { key: 'created_at', label: 'Created date' },
    { key: 'title', label: 'Title' },
    { key: 'labels', label: 'Labels' },
    { key: 'files', label: 'Files' },
  ];
  const activeColumnCount = COLUMNS.length + 1; // + Actions

  return (
    <div className="notes-page">
      <div className="notes-card">
        <div className="notes-header-row">
          <h1 className="notes-title">Notes (Private)</h1>
          <div className="notes-header-actions">
            <button
              type="button"
              className="notes-header-btn"
              onClick={() => setShowLabelsModal(true)}
            >
              Manage labels
            </button>
            <button
              type="button"
              className="notes-header-btn notes-header-btn-primary"
              onClick={openAddModal}
            >
              <IconPlus />
              Add note
            </button>
          </div>
        </div>

        {error && (
          <div className="notes-error" role="alert">
            {error}
          </div>
        )}

        <div className="notes-filters-row">
          <div className="notes-filters-left">
            <button
              type="button"
              className="notes-layout-toggle"
              aria-label="Layout options"
            >
              ≡
            </button>
          </div>

          <div className="notes-filters-right">
            <div className="notes-search">
              <input
                type="search"
                placeholder="Search"
                aria-label="Search notes"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <span className="notes-search-icon">
                <IconSearch />
              </span>
            </div>
          </div>
        </div>

        <div className="notes-table-wrapper">
          <table className="notes-table">
            <thead>
              <tr>
                {COLUMNS.map((col) => (
                  <th key={col.key}>{col.label}</th>
                ))}
                <th className="notes-th-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td className="notes-table-empty" colSpan={activeColumnCount}>
                    Loading…
                  </td>
                </tr>
              ) : filteredNotes.length === 0 ? (
                <tr>
                  <td className="notes-table-empty" colSpan={activeColumnCount}>
                    No record found.
                  </td>
                </tr>
              ) : (
                filteredNotes.map((note) => (
                  <tr key={note.id}>
                    <td>{formatDate(note.created_at)}</td>
                    <td>{note.title ?? '—'}</td>
                    <td>{note.labels ?? '—'}</td>
                    <td>
                      {note.attachment_url ? (
                        <a href={note.attachment_url} target="_blank" rel="noopener noreferrer" className="notes-attachment-link">
                          File
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="notes-actions-cell">
                      <button
                        type="button"
                        className="notes-row-btn"
                        onClick={() => openEditModal(note)}
                        title="Edit"
                        aria-label={`Edit ${note.title || 'note'}`}
                      >
                        <IconEdit />
                      </button>
                      <button
                        type="button"
                        className="notes-row-btn notes-row-btn-danger"
                        onClick={() => handleDelete(note)}
                        title="Delete"
                        aria-label={`Delete ${note.title || 'note'}`}
                      >
                        <IconTrash />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="notes-footer-row">
          <div className="notes-footer-left">
            <span className="notes-count">
              {loading ? '…' : `${filteredNotes.length} note${filteredNotes.length !== 1 ? 's' : ''}`}
            </span>
          </div>
        </div>
      </div>

      {showAddModal && (
        <div
          className="notes-modal-backdrop"
          onClick={closeAddModal}
        >
          <div className="notes-modal" onClick={(e) => e.stopPropagation()}>
            <div className="notes-modal-header">
              <h2 className="notes-modal-title">Add note</h2>
              <button
                type="button"
                className="notes-modal-close"
                onClick={closeAddModal}
                aria-label="Close"
              >
                <IconClose />
              </button>
            </div>

            <form onSubmit={handleSaveNote}>
              <div className="notes-modal-body">
                <div className="notes-form-group">
                  <input
                    type="text"
                    className="notes-form-input"
                    placeholder="Title"
                    aria-label="Title"
                    value={form.title}
                    onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  />
                </div>
                <div className="notes-form-group">
                  <textarea
                    className="notes-form-textarea"
                    placeholder="Description..."
                    rows={6}
                    aria-label="Description"
                    value={form.description}
                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  />
                </div>
                <div className="notes-form-group">
                  <input
                    type="text"
                    className="notes-form-input"
                    placeholder="Labels"
                    aria-label="Labels"
                    value={form.labels}
                    onChange={(e) => setForm((f) => ({ ...f, labels: e.target.value }))}
                  />
                </div>
                <div className="notes-form-group">
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="notes-file-input-hidden"
                    onChange={handleFileChange}
                    accept="*/*"
                    aria-label="Attach file"
                  />
                  {selectedFile ? (
                    <div className="notes-selected-file">
                      <span className="notes-selected-file-name" title={selectedFile.name}>
                        {selectedFile.name}
                      </span>
                      <span className="notes-selected-file-size">
                        ({(selectedFile.size / 1024).toFixed(1)} KB)
                      </span>
                      <button
                        type="button"
                        className="notes-remove-file"
                        onClick={() => { setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="notes-upload-btn notes-upload-btn-inline"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <IconUpload />
                      Upload File (max 5 MB)
                    </button>
                  )}
                </div>
              </div>

              <div className="notes-modal-footer">
                <div className="notes-modal-footer-left">
                  {!selectedFile && (
                    <button
                      type="button"
                      className="notes-upload-btn"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <IconUpload />
                      Upload File
                    </button>
                  )}
                  <button type="button" className="notes-mic-btn" aria-label="Voice input">
                    <IconMic />
                  </button>
                </div>
                <div className="notes-modal-footer-right">
                  <button
                    type="button"
                    className="notes-footer-btn notes-footer-btn-secondary"
                    onClick={closeAddModal}
                  >
                    <IconClose />
                    Close
                  </button>
                  <button
                    type="submit"
                    className="notes-footer-btn notes-footer-btn-primary"
                    disabled={saving}
                  >
                    <IconCheck />
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditModal && editingNote && (
        <div className="notes-modal-backdrop" onClick={closeEditModal}>
          <div className="notes-modal" onClick={(e) => e.stopPropagation()}>
            <div className="notes-modal-header">
              <h2 className="notes-modal-title">Edit note</h2>
              <button
                type="button"
                className="notes-modal-close"
                onClick={closeEditModal}
                aria-label="Close"
              >
                <IconClose />
              </button>
            </div>

            <form onSubmit={handleUpdateNote}>
              <div className="notes-modal-body">
                <div className="notes-form-group">
                  <input
                    type="text"
                    className="notes-form-input"
                    placeholder="Title"
                    aria-label="Title"
                    value={form.title}
                    onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  />
                </div>
                <div className="notes-form-group">
                  <textarea
                    className="notes-form-textarea"
                    placeholder="Description..."
                    rows={6}
                    aria-label="Description"
                    value={form.description}
                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  />
                </div>
                <div className="notes-form-group">
                  <input
                    type="text"
                    className="notes-form-input"
                    placeholder="Labels"
                    aria-label="Labels"
                    value={form.labels}
                    onChange={(e) => setForm((f) => ({ ...f, labels: e.target.value }))}
                  />
                </div>
                <div className="notes-form-group">
                  <input
                    ref={editFileInputRef}
                    type="file"
                    className="notes-file-input-hidden"
                    onChange={handleFileChange}
                    accept="*/*"
                    aria-label="Attach file"
                  />
                  {selectedFile ? (
                    <div className="notes-selected-file">
                      <span className="notes-selected-file-name" title={selectedFile.name}>
                        {selectedFile.name}
                      </span>
                      <span className="notes-selected-file-size">
                        ({(selectedFile.size / 1024).toFixed(1)} KB)
                      </span>
                      <button
                        type="button"
                        className="notes-remove-file"
                        onClick={() => { setSelectedFile(null); if (editFileInputRef.current) editFileInputRef.current.value = ''; }}
                      >
                        Remove
                      </button>
                    </div>
                  ) : editingNote.attachment_url && !attachmentRemoved ? (
                    <div className="notes-selected-file notes-current-attachment">
                      <span className="notes-selected-file-name">Current attachment</span>
                      <a href={editingNote.attachment_url} target="_blank" rel="noopener noreferrer" className="notes-attachment-link">Open</a>
                      <span className="notes-upload-replace"> · </span>
                      <button
                        type="button"
                        className="notes-remove-file"
                        onClick={() => setAttachmentRemoved(true)}
                      >
                        Remove file
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="notes-upload-btn notes-upload-btn-inline"
                      onClick={() => editFileInputRef.current?.click()}
                    >
                      <IconUpload />
                      {attachmentRemoved ? 'Add file (max 5 MB)' : 'Upload File (max 5 MB)'}
                    </button>
                  )}
                </div>
              </div>

              <div className="notes-modal-footer">
                <div className="notes-modal-footer-left">
                  {!selectedFile && (!editingNote.attachment_url || attachmentRemoved) && (
                    <button
                      type="button"
                      className="notes-upload-btn"
                      onClick={() => editFileInputRef.current?.click()}
                    >
                      <IconUpload />
                      Upload File
                    </button>
                  )}
                  <button type="button" className="notes-mic-btn" aria-label="Voice input">
                    <IconMic />
                  </button>
                </div>
                <div className="notes-modal-footer-right">
                  <button
                    type="button"
                    className="notes-footer-btn notes-footer-btn-secondary"
                    onClick={closeEditModal}
                  >
                    <IconClose />
                    Close
                  </button>
                  <button
                    type="submit"
                    className="notes-footer-btn notes-footer-btn-primary"
                    disabled={saving}
                  >
                    <IconCheck />
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="notes-modal-backdrop" onClick={() => setDeleteConfirm(null)}>
          <div className="notes-modal notes-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="notes-modal-header">
              <h2 className="notes-modal-title">Delete note</h2>
            </div>
            <div className="notes-modal-body">
              <p className="notes-confirm-text">
                Are you sure you want to delete <strong>{deleteConfirm.title || 'this note'}</strong>?
              </p>
            </div>
            <div className="notes-modal-footer notes-modal-footer-single">
              <button
                type="button"
                className="notes-footer-btn notes-footer-btn-secondary"
                onClick={() => setDeleteConfirm(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="notes-footer-btn notes-footer-btn-danger"
                onClick={confirmDelete}
                disabled={saving}
              >
                {saving ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showLabelsModal && (
        <div
          className="estimates-modal-backdrop"
          onClick={() => setShowLabelsModal(false)}
        >
          <div
            className="estimates-modal"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <div className="estimates-modal-header">
              <h2 className="estimates-modal-title">Manage labels</h2>
              <button
                type="button"
                className="estimates-modal-close"
                onClick={() => setShowLabelsModal(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="estimates-modal-body">
              <div className="notes-label-color-row">
                {labelPalette.map((color) => (
                  <div
                    key={color}
                    className={`notes-label-color-swatch${
                      selectedColor === color ? ' selected' : ''
                    }`}
                    style={{ background: color }}
                    onClick={() => setSelectedColor(color)}
                  />
                ))}
              </div>
              <div
                className="estimates-form-grid"
                style={{ gridTemplateColumns: '1fr auto' }}
              >
                <input
                  className="estimates-form-input"
                  placeholder="Label"
                  value={labelName}
                  onChange={(e) => setLabelName(e.target.value)}
                />
                <button
                  type="button"
                  className="estimates-footer-btn estimates-footer-btn-primary"
                  onClick={() => {
                    if (!labelName.trim()) return;
                    setLabels((prev) => [
                      ...prev,
                      { name: labelName.trim(), color: selectedColor },
                    ]);
                    setLabelName('');
                  }}
                >
                  Save
                </button>
              </div>
            </div>
            <div className="estimates-modal-footer">
              <button
                type="button"
                className="estimates-footer-btn"
                onClick={() => setShowLabelsModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

